import React from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";

// import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { CartBadge } from "@/components/business/cart/CartBadge";
import UserMenu from "@/components/ui/user-menu";
import Breadcrumbs, { BreadcrumbItem } from "@/components/ui/breadcrumbs";
import { cn } from "@/lib/utils";

interface NavigationHeaderProps {
  variant?: "public" | "dashboard" | "cajero" | "admin";
  title?: string;
  subtitle?: string;
  logo?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  showCart?: boolean;
  showThemeToggle?: boolean;
  showUserMenu?: boolean;
  userMenuVariant?: "compact" | "full";
  showMobileMenu?: boolean;
  onMobileMenuClick?: () => void;
  className?: string;
  onCartClick?: () => void;
}

const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  variant = "public",
  title = "Rincón Chilenito",
  subtitle,
  logo,
  breadcrumbs,
  showCart = true,
  // showThemeToggle = true,
  showUserMenu = true,
  userMenuVariant = "full",
  showMobileMenu = false,
  onMobileMenuClick,
  className,
  onCartClick,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "public":
        return "border-b border-border bg-card shadow-sm sticky top-0 z-50";
      case "dashboard":
        return "border-b border-border bg-card shadow-sm";
      case "cajero":
        return "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60";
      case "admin":
        return "border-b border-border bg-card shadow-sm";
      default:
        return "border-b border-border bg-card shadow-sm";
    }
  };

  const getLogo = () => {
    if (logo) return logo;

    const logoContent = (
      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-sm">
        <span className="text-primary-foreground font-bold text-sm">RC</span>
      </div>
    );

    return (
      <Link to="/" className="flex items-center space-x-2">
        {logoContent}
        <div className="flex flex-col justify-center text-left">
          <h1 className="text-lg md:text-xl font-bold text-card-foreground leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs text-muted-foreground hidden sm:block leading-tight">
              {subtitle}
            </p>
          )}
        </div>
      </Link>
    );
  };

  return (
    <header  className={cn(getVariantStyles(), className)}>
      <div  className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Logo y título - Alineado a la izquierda */}
          <div className="flex items-center space-x-3 flex-shrink-0 justify-start">{getLogo()}</div>

          {/* Spacer para empujar navegación a la derecha */}
          <div className="flex-1"></div>

          {/* Desktop Navigation - Alineado a la derecha */}
          <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
            {/* Carrito */}
            {showCart && <CartBadge showOnlyWhenItems={true} onClick={onCartClick} />}

            {/* Theme Toggle */}
            {/* {showThemeToggle && <ThemeToggle />} */}

            {/* User Menu */}
            {showUserMenu && <UserMenu variant={userMenuVariant} />}
          </div>

          {/* Mobile Navigation - Alineado a la derecha */}
          <div className="md:hidden flex items-center space-x-2 flex-shrink-0">
            {/* Botón Hamburguesa */}
            {showMobileMenu && onMobileMenuClick && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMobileMenuClick}
                className="p-2"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            
            {/* Carrito */}
            {showCart && <CartBadge showOnlyWhenItems={true} onClick={onCartClick} />}

            {/* Theme Toggle */}
            {/* {showThemeToggle && <ThemeToggle />} */}

            {/* User Menu Compact */}
            {showUserMenu && <UserMenu variant="compact" />}
          </div>
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}
      </div>
    </header>
  );
};

export default NavigationHeader;
